import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  CalendarMonth,
  CheckCircle,
  CreditCard,
  Lock,
  Star,
  SwapHoriz,
  Cancel,
  Undo,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { GET_STORE_BY_ID } from '../graphql/queries';
import {
  ACTIVATE_PLATFORM_SUBSCRIPTION,
  CANCEL_PLATFORM_SUBSCRIPTION,
  INIT_PLATFORM_BILLING,
  SWITCH_PLATFORM_BILLING_INTERVAL,
} from '../graphql/mutations';
import { useConfirmation } from '../hooks/useConfirmation';
import { CustomSnackbar } from '../components/common/CustomSnackbar';
import LoadingSpinner from '../components/common/LoadingSpinner';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const MONTHLY_PRICE = 50;
const ANNUAL_PRICE = 540;
const UPFRONT_MONTHS = 3;

type BillingInterval = 'monthly' | 'annual';

// ─── Card form (must live inside <Elements>) ─────────────────────────────────

interface CardFormProps {
  billingInterval: BillingInterval;
  clientSecret: string;
  intentType: 'payment' | 'setup';
  onSuccess: (paymentMethodId: string, paymentIntentId?: string) => void;
  onCancel: () => void;
  busy: boolean;
}

const CardForm = ({
  billingInterval,
  clientSecret,
  intentType,
  onSuccess,
  onCancel,
  busy,
}: CardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setConfirming(true);
    setCardError(null);

    if (intentType === 'payment') {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });
      setConfirming(false);
      if (error) {
        setCardError(error.message ?? 'Payment failed. Please try again.');
      } else if (paymentIntent?.status === 'succeeded') {
        const pmId = typeof paymentIntent.payment_method === 'string'
          ? paymentIntent.payment_method
          : paymentIntent.payment_method?.id ?? '';
        onSuccess(pmId, paymentIntent.id);
      }
    } else {
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });
      setConfirming(false);
      if (error) {
        setCardError(error.message ?? 'Card setup failed. Please try again.');
      } else if (setupIntent?.payment_method) {
        const pmId = typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;
        onSuccess(pmId);
      }
    }
  };

  const working = confirming || busy;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Enter card details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {billingInterval === 'monthly'
          ? `You will be charged £${MONTHLY_PRICE * UPFRONT_MONTHS} now (${UPFRONT_MONTHS} months upfront), then £${MONTHLY_PRICE}/month from month 4.`
          : `You will be charged £${ANNUAL_PRICE} now, then £${ANNUAL_PRICE}/year on renewal.`}
      </Typography>

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          mb: 2,
          bgcolor: 'background.paper',
        }}
      >
        <CardElement
          options={{
            style: {
              base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
              invalid: { color: '#9e2146' },
            },
          }}
        />
      </Box>

      {cardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cardError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="text" onClick={onCancel} disabled={working} sx={{ fontWeight: 600 }}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={working || !stripe}
          startIcon={working ? <CircularProgress size={16} color="inherit" /> : <CreditCard />}
          sx={{ borderRadius: 2, px: 3, py: 1.25, fontWeight: 600 }}
        >
          {working
            ? 'Processing…'
            : billingInterval === 'monthly'
              ? `Pay £${MONTHLY_PRICE * UPFRONT_MONTHS} & subscribe`
              : `Pay £${ANNUAL_PRICE} & subscribe`}
        </Button>
      </Stack>
    </Box>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const ACTIVE_STATUSES = ['active', 'trialing'];

const PlatformSubscription = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const storeId = paramStoreId || authStoreId;

  const { snackbarState, closeSnackbar, showSuccessSnackbar, showErrorSnackbar } =
    useConfirmation();

  const { data, loading, refetch } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId },
    skip: !storeId,
    fetchPolicy: 'cache-and-network',
  });

  const store = data?.storeDetails;
  const status: string = store?.platformSubscriptionStatus ?? 'inactive';
  const billingInterval: string | null = store?.platformBillingInterval ?? null;
  const cancelAtPeriodEnd: boolean = store?.platformSubscriptionCancelAtPeriodEnd ?? false;
  const isActive = ACTIVE_STATUSES.includes(status);

  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>('monthly');
  const [cardSetup, setCardSetup] = useState<{
    clientSecret: string;
    intentType: 'payment' | 'setup';
  } | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState<BillingInterval | null>(null);

  const [initBilling, { loading: initing }] = useMutation(INIT_PLATFORM_BILLING);
  const [activateSub, { loading: activating }] = useMutation(ACTIVATE_PLATFORM_SUBSCRIPTION);
  const [cancelSub, { loading: canceling }] = useMutation(CANCEL_PLATFORM_SUBSCRIPTION);
  const [switchInterval, { loading: switching }] = useMutation(SWITCH_PLATFORM_BILLING_INTERVAL);

  const handleSubscribe = async () => {
    try {
      const result = await initBilling({
        variables: { storeId, billingInterval: selectedInterval },
      });
      const payload = result.data?.initPlatformBilling;
      if (!payload?.ok || !payload?.clientSecret) {
        showErrorSnackbar(payload?.errors?.[0] ?? 'Could not initialise billing. Try again.');
        return;
      }
      setCardSetup({ clientSecret: payload.clientSecret, intentType: payload.intentType });
    } catch {
      showErrorSnackbar('Could not initialise billing. Please try again.');
    }
  };

  const handleCardSuccess = async (paymentMethodId: string, paymentIntentId?: string) => {
    try {
      const result = await activateSub({
        variables: {
          storeId,
          billingInterval: selectedInterval,
          paymentMethodId,
          paymentIntentId,
        },
      });
      const payload = result.data?.activatePlatformSubscription;
      if (payload?.ok) {
        showSuccessSnackbar('Subscription activated! Welcome aboard.');
        setCardSetup(null);
        await refetch();
        setTimeout(() => navigate(`/store/${storeId}/dashboard`), 1500);
      } else {
        showErrorSnackbar(payload?.errors?.[0] ?? 'Subscription activation failed.');
      }
    } catch {
      showErrorSnackbar('Subscription activation failed. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    setShowCancelConfirm(false);
    try {
      const result = await cancelSub({ variables: { storeId, cancelAtPeriodEnd: true } });
      const payload = result.data?.cancelPlatformSubscription;
      if (payload?.ok) {
        showSuccessSnackbar('Subscription scheduled to cancel at period end. No further charges will be made.');
        await refetch();
      } else {
        showErrorSnackbar(payload?.errors?.[0] ?? 'Could not cancel subscription.');
      }
    } catch {
      showErrorSnackbar('Could not cancel subscription. Please try again.');
    }
  };

  const handleUndoCancel = async () => {
    try {
      const result = await cancelSub({ variables: { storeId, cancelAtPeriodEnd: false } });
      const payload = result.data?.cancelPlatformSubscription;
      if (payload?.ok) {
        showSuccessSnackbar('Cancellation removed — your subscription will continue.');
        await refetch();
      } else {
        showErrorSnackbar(payload?.errors?.[0] ?? 'Could not update subscription.');
      }
    } catch {
      showErrorSnackbar('Could not update subscription. Please try again.');
    }
  };

  const handleSwitchInterval = async (newInterval: BillingInterval) => {
    setShowSwitchConfirm(null);
    try {
      const result = await switchInterval({ variables: { storeId, billingInterval: newInterval } });
      const payload = result.data?.switchPlatformBillingInterval;
      if (payload?.ok) {
        showSuccessSnackbar(`Switched to ${newInterval} billing — effective from your next renewal date.`);
        await refetch();
      } else {
        showErrorSnackbar(payload?.errors?.[0] ?? 'Could not switch billing interval.');
      }
    } catch {
      showErrorSnackbar('Could not switch billing interval. Please try again.');
    }
  };

  if (loading && !store) return <LoadingSpinner message="Loading…" />;
  if (!storeId || !store) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Store not found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: '760px',
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <IconButton
            component={Link}
            to={`/store/${storeId}/dashboard`}
            size="small"
            aria-label="Back to dashboard"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="overline" color="text.secondary">
            Dashboard
          </Typography>
        </Stack>
        <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 1 }}>
          Platform subscription
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          A platform subscription is required to keep your store active and accessible to clients.
        </Typography>

        {/* ── Already subscribed ── */}
        {isActive && (
          <>
            {/* Cancellation warning banner */}
            {cancelAtPeriodEnd && (
              <Alert
                severity="warning"
                sx={{ mb: 2, borderRadius: 2 }}
                action={
                  <Button
                    size="small"
                    startIcon={canceling ? <CircularProgress size={14} color="inherit" /> : <Undo />}
                    onClick={handleUndoCancel}
                    disabled={canceling}
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    Keep subscription
                  </Button>
                }
              >
                Your subscription is scheduled to cancel at the end of this billing period. No further charges will be made.
              </Alert>
            )}

            {/* Current plan card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <CheckCircle color="success" />
                  <Typography variant="h6" fontWeight={700}>
                    Subscription active
                  </Typography>
                  <Chip
                    label={status === 'trialing' ? 'Trial period' : 'Active'}
                    color={status === 'trialing' ? 'warning' : 'success'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Plan</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {billingInterval === 'annual'
                        ? `Annual · £${ANNUAL_PRICE}/year`
                        : `Monthly · £${MONTHLY_PRICE}/month`}
                    </Typography>
                  </Stack>
                  {status === 'trialing' && billingInterval === 'monthly' && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Prepaid period</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        3 months (billing starts month 4)
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {/* Switch interval */}
                {!cancelAtPeriodEnd && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Switch billing interval
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      The change takes effect from your next renewal — no refund on the current period.
                    </Typography>
                    {billingInterval === 'monthly' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={switching ? <CircularProgress size={14} color="inherit" /> : <SwapHoriz />}
                        onClick={() => setShowSwitchConfirm('annual')}
                        disabled={switching}
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      >
                        Switch to annual (£{ANNUAL_PRICE}/year · save 10%)
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={switching ? <CircularProgress size={14} color="inherit" /> : <SwapHoriz />}
                        onClick={() => setShowSwitchConfirm('monthly')}
                        disabled={switching}
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      >
                        Switch to monthly (£{MONTHLY_PRICE}/month)
                      </Button>
                    )}
                  </>
                )}

                {/* Cancel */}
                {!cancelAtPeriodEnd && (
                  <>
                    <Divider sx={{ mt: 2, mb: 2 }} />
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => setShowCancelConfirm(true)}
                      sx={{ fontWeight: 600 }}
                    >
                      Cancel subscription
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Cancel confirmation dialog */}
            {showCancelConfirm && (
              <Card sx={{ mb: 3, borderRadius: 2, border: 2, borderColor: 'error.main', boxShadow: theme.shadows[2] }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography variant="h6" fontWeight={700} color="error" sx={{ mb: 1 }}>
                    Cancel your subscription?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your store will remain active until the end of the current billing period.
                    No refund will be issued for the current period. After cancellation, your
                    admin panel will be locked until you resubscribe.
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={() => setShowCancelConfirm(false)}
                      sx={{ fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Keep subscription
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleCancelSubscription}
                      disabled={canceling}
                      startIcon={canceling ? <CircularProgress size={14} color="inherit" /> : <Cancel />}
                      sx={{ fontWeight: 600, borderRadius: 1.5 }}
                    >
                      {canceling ? 'Cancelling…' : 'Yes, cancel'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Switch interval confirmation dialog */}
            {showSwitchConfirm && (
              <Card sx={{ mb: 3, borderRadius: 2, border: 2, borderColor: 'primary.main', boxShadow: theme.shadows[2] }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    Switch to {showSwitchConfirm} billing?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    You will be charged{' '}
                    <strong>
                      {showSwitchConfirm === 'annual' ? `£${ANNUAL_PRICE}/year` : `£${MONTHLY_PRICE}/month`}
                    </strong>{' '}
                    starting from your next renewal date. No refund is issued for the current period.
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={() => setShowSwitchConfirm(null)}
                      sx={{ fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleSwitchInterval(showSwitchConfirm)}
                      disabled={switching}
                      startIcon={switching ? <CircularProgress size={14} color="inherit" /> : <SwapHoriz />}
                      sx={{ fontWeight: 600, borderRadius: 1.5 }}
                    >
                      {switching ? 'Switching…' : `Confirm switch to ${showSwitchConfirm}`}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ── Inactive / past_due / canceled — show plan picker or card form ── */}
        {!isActive && !cardSetup && (
          <>
            {status === 'past_due' && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                Your last payment failed. Please subscribe again to restore access.
              </Alert>
            )}
            {status === 'canceled' && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                Your subscription was cancelled. Re-subscribe below to restore access.
              </Alert>
            )}

            {/* Plan selector */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              {/* Monthly card */}
              <Card
                onClick={() => setSelectedInterval('monthly')}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedInterval === 'monthly' ? 'primary.main' : 'divider',
                  boxShadow: selectedInterval === 'monthly' ? theme.shadows[4] : theme.shadows[1],
                  transition: 'all 0.2s',
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="overline" color="text.secondary">Monthly</Typography>
                      <Typography variant="h4" fontWeight={700}>
                        £{MONTHLY_PRICE}
                        <Typography component="span" variant="body1" color="text.secondary">
                          /month
                        </Typography>
                      </Typography>
                    </Box>
                    <CreditCard color={selectedInterval === 'monthly' ? 'primary' : 'disabled'} />
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Lock fontSize="small" color="action" />
                      <Typography variant="body2">
                        £{MONTHLY_PRICE * UPFRONT_MONTHS} upfront ({UPFRONT_MONTHS} months)
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Typography variant="body2">
                        £{MONTHLY_PRICE}/month from month 4
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {/* Annual card */}
              <Card
                onClick={() => setSelectedInterval('annual')}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedInterval === 'annual' ? 'primary.main' : 'divider',
                  boxShadow: selectedInterval === 'annual' ? theme.shadows[4] : theme.shadows[1],
                  transition: 'all 0.2s',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <Chip
                  label="Save 10%"
                  color="success"
                  size="small"
                  icon={<Star fontSize="small" />}
                  sx={{
                    position: 'absolute',
                    top: -12,
                    right: 16,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                  }}
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="overline" color="text.secondary">Annual</Typography>
                      <Typography variant="h4" fontWeight={700}>
                        £{ANNUAL_PRICE}
                        <Typography component="span" variant="body1" color="text.secondary">
                          /year
                        </Typography>
                      </Typography>
                    </Box>
                    <CalendarMonth color={selectedInterval === 'annual' ? 'primary' : 'disabled'} />
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircle fontSize="small" color="action" />
                      <Typography variant="body2">
                        No 3-month commitment — pay annually
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Star fontSize="small" color="action" />
                      <Typography variant="body2">
                        Equivalent to £{(ANNUAL_PRICE / 12).toFixed(2)}/month
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSubscribe}
                disabled={initing}
                startIcon={initing ? <CircularProgress size={16} color="inherit" /> : <CreditCard />}
                sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 600, fontSize: '1rem' }}
              >
                {initing ? 'Processing…' : 'Continue to payment'}
              </Button>
            </Stack>
          </>
        )}

        {/* ── Card entry step ── */}
        {!isActive && cardSetup && (
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Elements stripe={stripePromise} options={{ locale: 'en-GB' }}>
                <CardForm
                  billingInterval={selectedInterval}
                  clientSecret={cardSetup.clientSecret}
                  intentType={cardSetup.intentType}
                  onSuccess={handleCardSuccess}
                  onCancel={() => setCardSetup(null)}
                  busy={activating}
                />
              </Elements>
            </CardContent>
          </Card>
        )}
      </motion.div>

      <CustomSnackbar
        snackbar={{
          open: snackbarState.open,
          message: snackbarState.message,
          severity: snackbarState.severity,
          title: snackbarState.title,
          autoHideDuration: snackbarState.autoHideDuration,
        }}
        onClose={closeSnackbar}
      />
    </Box>
  );
};

export default PlatformSubscription;
